'use client';

import { cn, formatDateTime, nameof } from '@/lib/utils';
import { AppSysNotificationResponse } from '@/server/api/v1/system-notification/system-notification.routes';
import { LogicalOperator, OperatorType } from '@/server/base/base-trpc-service';
import { SocketEndpoint } from '@/signalR/enum';
import { useSocketListener } from '@/signalR/use-socket-listener';
import { api } from '@/trpc/client';
import { Volume2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import CustomDialog from '../custom-dialog';
import Marquee from '../ui/marquee';

type SystemNotice = {
    name: string;
    activeDay?: string;
    color?: string;
};

export const SystemNotices = ({
    className,
    sysNotices,
}: {
    className?: string;
    sysNotices?: AppSysNotificationResponse[];
}) => {
    const transDef = useTranslations('System');
    const apiUtils = api.useUtils();

    const [items, setItems] = useState<SystemNotice[]>([]);

    const onFetchSysNotice = useCallback(async () => {
        const result = await apiUtils.appSysNotication.Search.fetch({
            filters: [
                {
                    key: nameof<AppSysNotificationResponse>('IsActive'),
                    values: ['true'],
                    logicalOperator: LogicalOperator.And,
                    operatorType: OperatorType.Equal,
                },
            ],
        });

        if (result.isSuccessStatus && result.data?.Items.length) {
            setItems(
                result.data.Items.map((x) => {
                    return {
                        name: `${x.Title}: ${x.Value}`,
                        activeDay: x.ActiveAtDate,
                        color: 'red',
                    };
                })
            );
        } else {
            setItems([]);
        }
    }, [apiUtils.appSysNotication.Search]);

    useEffect(() => {
        if (typeof sysNotices !== 'undefined') {
            setItems(
                sysNotices.map((x) => {
                    return {
                        name: `${x.Title}: ${x.Value}`,
                        activeDay: x.ActiveAtDate,
                        color: 'blue',
                    };
                })
            );
        } else {
            onFetchSysNotice();
        }
    }, [onFetchSysNotice, sysNotices]);

    const onListenSendSysNotice = useCallback(
        (data: any[]) => {
            //Nếu có thông báo hệ thống thay đổi, refetch lại
            onFetchSysNotice();
        },
        [onFetchSysNotice]
    );

    useSocketListener({
        endPoint: SocketEndpoint.hubs,
        listener: {
            SendSysNotification: onListenSendSysNotice,
        },
    });

    if (items.length == 0) return <div></div>;
    return (
        <div
            className={cn(
                'relative flex flex-1 items-center overflow-hidden',
                className
            )}
        >
            <div className='absolute z-[1] hidden h-7 w-5 overflow-hidden md:flex'>
                <div className='rotate-45 border-b-[20px] border-r-[2px] border-t-[20px] border-secondary'></div>
                <div className='absolute left-0 border-r-[20px] border-t-[20px] border-card border-l-transparent border-r-transparent'></div>
            </div>
            {/*TODO: tính duration lại dựa trên số ký tự của tất cả phần tử con */}
            <CustomDialog
                headerTitle={transDef('System_SystemNotification')}
                modalSize='3xl'
                trigger={
                    <div>
                        <Marquee pauseOnHover className='[--duration:50s]'>
                            {items.map((x, i) => (
                                <div
                                    className='flex items-center gap-2 text-nowrap hover:cursor-pointer'
                                    key={i}
                                >
                                    <Volume2 className='w-4' />
                                    <i
                                        className={cn(
                                            'text-sm text-primary dark:text-foreground'
                                        )}
                                    >
                                        {x.name}
                                    </i>
                                </div>
                            ))}
                        </Marquee>
                    </div>
                }
            >
                <div className='min-h-96 max-lg:py-3 lg:min-h-32'>
                    {items.map((x, i) => (
                        <div
                            key={i}
                            style={{ color: x.color }}
                            className='mb-3'
                        >
                            {x.activeDay && (
                                <p className='font-semibold'>
                                    [{formatDateTime(x.activeDay)}]{' '}
                                </p>
                            )}
                            <p className=''>{x.name}</p>
                        </div>
                    ))}
                </div>
            </CustomDialog>
        </div>
    );
};
